<template>
  <div ref="wrapperRef" class="h-full flex items-center" @keydown.esc="close">
    <p aria-live="polite" class="sr-only">
      {{ announcement }}
    </p>
    <!-- Mobile Search Icon -->
    <icon-search
      v-model="showMobileForm"
      aria-controls="vf-search"
      :aria-expanded="showMobileForm"
      class="mx-2 cursor-pointer lg:hidden"
      :size="mobileNavIconSize"
      type="button"
      @click="toggleSearch"
    >
      <span class="sr-only">{{ $t.searchIconLabel }}</span>
    </icon-search>
    <!-- Backdrop -->
    <div
      v-style:top="`calc(${pxToRem(offset)} + 0.5rem)`"
      class="fixed-0 h-full bg-black/75"
      :class="{ '<lg:hidden': !showMobileForm, 'lg:hidden': !showSuggestions }"
      @click.self="close"
    />
    <div id="vf-search" :class="showMobileForm ? '<lg:absolute-0 <lg:mt-14' : '<lg:hidden'">
      <!-- Search Bar -->
      <vf-search-bar
        ref="formRef"
        v-model="searchValue"
        :class="[brandClasses.searchBar, { 'b-white': isTransparent }]"
        @click="handleOpenSuggestions"
        @focus="handleOpenSuggestions"
        @submit="submit"
      />
      <!-- Search Suggestions -->
      <transition
        enter-active-class="transform ease-out"
        enter-from-class="op-0"
        leave-active-class="transform ease-in"
        leave-to-class="op-0"
      >
        <div
          v-if="showSuggestions"
          ref="suggestionsRef"
          :class="brandClasses.suggestions?.wrapper"
          :style="{
            'max-height': `calc(100dvh - ${pxToRem(y)} - ${pxToRem(offset)} + 0.5rem)`,
            'position': strategy,
            'top': pxToRem(y),
            'left': pxToRem(x),
          }"
        >
          <section
            :aria-label="$t.searchResults"
            :class="brandClasses.suggestions?.results"
            style="min-height: 20.5rem"
          >
            <div v-if="showSuggestions" class="space-y-6">
              <h2 class="mb-6 <lg:px-4" :class="brandClasses.suggestions?.title">
                {{ $t.featuredResults }}
              </h2>
              <ul :class="brandClasses.suggestions?.products">
                <li
                  v-for="{ id, name, images, currency, price, url } in suggestions?.products"
                  :key="id"
                  :class="brandClasses.suggestions?.item"
                >
                  <base-picture
                    v-if="images"
                    :alt="name"
                    :class="brandClasses.suggestions?.picture"
                    height="88"
                    :src="images"
                    width="88"
                  />
                  <div class="text-sm">
                    <base-link
                      class="line-clamp-2 mb-2 fw-medium"
                      :to="{
                        ...url,
                        state: {
                          ...url.state,
                          searchType: 'Featured Results',
                        },
                      }"
                      @click="handleProductClick"
                    >
                      {{ name }}
                      <span class="absolute-0" />
                    </base-link>
                    <product-pricing :currency :price />
                  </div>
                </li>
              </ul>
            </div>
            <div v-if="showSuggestions" class="<md:order-first <md:px-4 space-y-4">
              <h2 :class="brandClasses.suggestions?.title">
                {{ $t.topSearches }}
              </h2>
              <ul class="text-sm space-y-2">
                <li v-for="{ label, highlightedLabel } in suggestions?.terms" :key="label">
                  <base-link
                    :aria-label="label"
                    :to="{
                      path: '/search',
                      query: { q: label },
                      state: {
                        searchType: 'Top Searches',
                        suggestedPhrase: label,
                      },
                    }"
                    @click="reset"
                  >
                    <span v-html="highlightedLabel" />
                  </base-link>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AutoSuggestData } from '#root/api/clients/product/data-contracts'
import type { RouteLocationRaw } from 'vue-router'
import { autoUpdate, shift, useFloating } from '@floating-ui/vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import defu from 'defu'

type AutoSuggestDataWithUrlObjects = Omit<AutoSuggestData, 'products'> & {
  products: (Omit<NonNullable<AutoSuggestData['products']>[number], 'url'> & {
    url: RouteLocationRaw
  })[] | undefined
}

defineProps<{
  /**
   * Defines color of navigation menu is white or not
   */
  isTransparent?: boolean
}>()

const emit = defineEmits<{
  /**
   * Emits when search is submitted
   */
  search: [query: string]
}>()

const opened = defineModel('opened', { default: false })

const wrapperRef = ref<HTMLDivElement>()
const formRef = ref<HTMLFormElement>()
const suggestionsRef = ref<HTMLDivElement>()

const {
  brandClasses,
  lockBodyScroll,
  maxTopSearches = 7,
  mobileNavIconSize,
  searchThreshold = 0,
} = useAppConfig().components.vf.search
const { lock, unlock } = useBodyScroll()
const { bottom: offset, update: updateOffset } = useElementBounding(wrapperRef)
const { $gtm, $t, $viewport } = useNuxtApp()
const storeFilter = useRouteQuery<string | undefined>('storeFilter')
const router = useRouter()

const announcement = ref('')
const searchValue = ref('')
const showMobileForm = ref(false)
const showSuggestions = ref(false)
const suggestionsQuery = reactive({ q: searchValue.value })

let abortSuggestions = false

const focus = () => formRef.value?.inputRef?.$el.focus()

const clear = (resetFocus = false) => {
  searchValue.value = ''
  if (resetFocus) focus()
}

const close = () => {
  showMobileForm.value = false
  showSuggestions.value = false
}

const toggleSearch = () => {
  if (!showMobileForm.value)
    $gtm.push('topNav.onClickUtilityLink', 'Search')
  showMobileForm.value = !showMobileForm.value
}

const reset = () => {
  close()
  clear()
}

const handleProductClick = () => {
  setInteractionOrigin('search')
  reset()
}

const { activate, deactivate } = useFocusTrap(wrapperRef, {
  clickOutsideDeactivates: true,
  escapeDeactivates: false,
  onActivate: () => nextTick(focus),
  onDeactivate: close
})

const { x, y, strategy } = useFloating(formRef, suggestionsRef, {
  placement: 'bottom-end',
  whileElementsMounted: autoUpdate,
  middleware: [shift()]
})

const suggestionsAvailable = (data: AutoSuggestDataWithUrlObjects) =>
  data && (!!data.products?.length || !!data.terms?.filter(({ label }) => label !== suggestionsQuery.q).length)

const { data: suggestions, pending: suggestionsPending } = useApi().products.autoSuggest(suggestionsQuery, {
  immediate: false,
  server: false,
  transform({ products, terms }) {
    const termRegExp = new RegExp(`(${searchValue.value})`, 'gi')
    return {
      products: products?.slice(0, 4).map(({ url, ...product }) => {
        const updatedUrl = defu(getRouteFromUrl(url), {
          query: {
            ...(storeFilter.value ? { storeFilter: storeFilter.value } : {}),
          },
          state: {
            category: 'Search: Featured Results',
            suggestedPhrase: searchValue.value,
          },
        })

        return {
          ...product,
          url: updatedUrl
        }
      }),
      terms: terms?.filter(({ label }) => label).slice(0, Math.min(maxTopSearches, 7)).map(({ label }) => ({
        label,
        highlightedLabel: label?.replace(termRegExp, '<span class="fw-medium">$1</span>')
      }))
    }
  },
  onResponse({ response: { _data } }) {
    showSuggestions.value = suggestionsAvailable(_data) && !abortSuggestions
  }
})

const handleOpenSuggestions = () => {
  if (searchValue.value.length >= searchThreshold && suggestionsAvailable(suggestions.value!))
    showSuggestions.value = true
}

const submit = () => {
  if (searchValue.value) {
    abortSuggestions = suggestionsPending.value
    emit('search', searchValue.value)
    router.push(useLocalisedRoute(`/search?q=${searchValue.value}`))
    reset()
  }
}

const onInput = useDebounceFn(() => {
  // hide suggestions if input length is below a threshold
  if (searchValue.value.length < searchThreshold) {
    showSuggestions.value = false
    return
  }

  // show previously fetched suggestions if the same search is re-entered
  if (searchValue.value === suggestionsQuery.q && !!suggestions) {
    showSuggestions.value = true
    return
  }
  // reset abort boolean when making a new search
  abortSuggestions = false
  suggestionsQuery.q = searchValue.value

  if (!searchValue.value && $viewport.md) close()
}, 100)

const setAnnouncement = useDebounceFn(() => {
  const results = suggestions.value?.products?.length || 0
  const terms = suggestions.value?.terms?.length || 0
  announcement.value = showSuggestions.value
    ? `${results} ${$t.featuredResults}, ${terms} ${$t.topSearches} ${$t.for} ${suggestionsQuery.q}`
    : ''
}, 1000)

watch([showMobileForm, showSuggestions], () => {
  if (showMobileForm.value || showSuggestions.value) {
    activate()
    opened.value = true
    if (lockBodyScroll) lock()
    handleOpenSuggestions()
  }
  else {
    deactivate()
    opened.value = false
    if (lockBodyScroll) unlock()
  }
})

watch(showSuggestions, updateOffset)
watch(searchValue, onInput)
watch(suggestions, setAnnouncement)

// close suggestions and clear search if route changes
router.beforeEach(reset)
</script>
